"use strict";
/**
 * InkStep Mnemonic Dictionary & Phonics Content Engine.
 *
 * Provides canonical, copyright-cleared pedagogical metadata for all 50
 * graphemes in the InkStep systematic synthetic phonics (SSP) progression:
 * - Position (1-50) & Stage (Set 1, Set 2, Set 3)
 * - Mnemonic keyword (e.g. "Mountain", "Apple", "Snake")
 * - Letter formation rhyme (child-friendly UK handwriting guidance)
 * - Pure-phoneme pronunciation hint (for teachers, parents, and TAs)
 * - Clean SVG vector illustration (for sound cards, practice sheets, and previews)
 * - Decodable and example words
 * - Canonical public HTTPS audio player URL resolver
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.INKSTEP_MNEMONICS = void 0;
exports.getInkStepPublicAudioUrl = getInkStepPublicAudioUrl;
exports.getInkStepMnemonic = getInkStepMnemonic;
exports.getAllInkStepMnemonics = getAllInkStepMnemonics;
exports.getInkStepMnemonicsForStage = getInkStepMnemonicsForStage;
/**
 * Creates a simple SVG icon markup for the mnemonic illustration.
 * Uses high-contrast 2-color vector paths suitable for both color screen previews
 * and monochrome laser printing (ink-saver).
 */
function createSvgIcon(name, pathData, accentPathData = '') {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100" class="w-full h-full" aria-label="${name} illustration">
        <rect width="96" height="96" x="2" y="2" rx="16" fill="#F8FAFC" stroke="#CBD5E1" stroke-width="2"/>
        <path d="${pathData}" fill="#1E293B" fill-rule="evenodd"/>
        ${accentPathData ? `<path d="${accentPathData}" fill="#4F46E5"/>` : ''}
    </svg>`;
}
exports.INKSTEP_MNEMONICS = {
    // ── Set 1: Simple sounds & early digraphs (positions 1-31) ──
    'm': {
        position: 1,
        grapheme: 'm',
        stageName: 'Set 1',
        keyword: 'Mountain',
        formationRhyme: 'Down the mast, over the mountain, over the mountain.',
        purePhonemeHint: 'Press lips together and hum: /m/ (as in "mmm delicious"). Avoid saying "muh".',
        exampleWords: ['mat', 'man', 'mud', 'mop'],
        sampleDecodables: ['mat', 'map'],
        svgIcon: createSvgIcon('Mountain', 'M20 75 L38 35 L50 55 L68 25 L85 75 Z', 'M38 35 L44 48 L32 48 Z M68 25 L74 38 L62 38 Z'),
    },
    'a': {
        position: 2,
        grapheme: 'a',
        stageName: 'Set 1',
        keyword: 'Apple',
        formationRhyme: 'Around the apple, down the stem, and a little flick.',
        purePhonemeHint: 'Open mouth wide: short, crisp /a/ (as in "ant"). Avoid saying "ay".',
        exampleWords: ['ant', 'apple', 'ask', 'act'],
        sampleDecodables: ['at', 'am'],
        svgIcon: createSvgIcon('Apple', 'M50 30 C30 30 25 50 25 65 C25 80 40 85 50 85 C60 85 75 80 75 65 C75 50 70 30 50 30 Z', 'M50 30 C50 18 60 15 62 14 C60 22 55 26 50 30 Z'),
    },
    's': {
        position: 3,
        grapheme: 's',
        stageName: 'Set 1',
        keyword: 'Snake',
        formationRhyme: 'Slither and slide down the winding snake.',
        purePhonemeHint: 'Continuous unvoiced hiss: /s/ (as in "sss snake"). Avoid saying "suh".',
        exampleWords: ['sun', 'sat', 'sip', 'soap'],
        sampleDecodables: ['sat', 'sam'],
        svgIcon: createSvgIcon('Snake', 'M65 25 C55 20 40 22 38 34 C36 44 65 46 64 62 C63 76 45 80 32 72 L36 62 C45 68 54 66 54 58 C54 48 28 46 30 30 C32 15 50 12 68 18 Z'),
    },
    'd': {
        position: 4,
        grapheme: 'd',
        stageName: 'Set 1',
        keyword: 'Dinosaur',
        formationRhyme: 'Around the dinosaur\'s tummy, up his tall neck, and down to his feet.',
        purePhonemeHint: 'Tongue taps the roof of the mouth: clipped /d/. Avoid saying "duh".',
        exampleWords: ['dad', 'dog', 'dig', 'dot'],
        sampleDecodables: ['dad', 'sad'],
        svgIcon: createSvgIcon('Dinosaur', 'M35 50 C25 50 20 62 20 72 C20 82 30 85 45 85 C60 85 70 78 70 65 L70 20 L60 20 L60 52 C54 50 46 50 35 50 Z'),
    },
    't': {
        position: 5,
        grapheme: 't',
        stageName: 'Set 1',
        keyword: 'Tower',
        formationRhyme: 'Down the tall tower, curl the bottom, and cross in the middle.',
        purePhonemeHint: 'Crisp unvoiced tap: /t/ (like a clock tick). Avoid saying "tuh".',
        exampleWords: ['tap', 'top', 'tin', 'ten'],
        sampleDecodables: ['tap', 'sat'],
        svgIcon: createSvgIcon('Tower', 'M45 15 L55 15 L55 35 L75 35 L75 45 L55 45 L55 75 C55 80 58 82 65 82 L70 82 L70 90 C62 90 45 90 45 76 L45 45 L30 45 L30 35 L45 35 Z'),
    },
    'i': {
        position: 6,
        grapheme: 'i',
        stageName: 'Set 1',
        keyword: 'Insect',
        formationRhyme: 'Down the insect\'s body, and a dot for its head.',
        purePhonemeHint: 'Short open sound: /i/ (as in "igloo", "in").',
        exampleWords: ['in', 'it', 'ink', 'ill'],
        sampleDecodables: ['it', 'sit'],
        svgIcon: createSvgIcon('Insect', 'M44 40 L56 40 L56 80 L44 80 Z', 'M50 20 A 7 7 0 1 0 50 34 A 7 7 0 1 0 50 20 Z'),
    },
    'n': {
        position: 7,
        grapheme: 'n',
        stageName: 'Set 1',
        keyword: 'Net',
        formationRhyme: 'Down the post, up and over the net.',
        purePhonemeHint: 'Continuous nasal hum: /n/ with tongue against the roof. Avoid "nuh".',
        exampleWords: ['net', 'nut', 'nod', 'nip'],
        sampleDecodables: ['pan', 'pin'],
        svgIcon: createSvgIcon('Net', 'M20 30 L80 30 L80 75 L20 75 Z M30 30 L30 75 M45 30 L45 75 M60 30 L60 75 M20 45 L80 45 M20 60 L80 60'),
    },
    'p': {
        position: 8,
        grapheme: 'p',
        stageName: 'Set 1',
        keyword: 'Pirate',
        formationRhyme: 'Down the pirate\'s plait, back up and around his face.',
        purePhonemeHint: 'Unvoiced burst of air: /p/ (like popping bubbles). Avoid saying "puh".',
        exampleWords: ['pan', 'pig', 'pot', 'pen'],
        sampleDecodables: ['pat', 'tap'],
        svgIcon: createSvgIcon('Pirate', 'M35 25 L45 25 L45 90 L35 90 Z M45 25 C65 25 75 35 75 48 C75 62 65 70 45 70 Z'),
    },
    'g': {
        position: 9,
        grapheme: 'g',
        stageName: 'Set 1',
        keyword: 'Girl',
        formationRhyme: 'Around the girl\'s face, down her long ponytail, and give it a curl.',
        purePhonemeHint: 'Throat tap: clipped /g/ (as in "gargle"). Avoid saying "guh".',
        exampleWords: ['gap', 'got', 'gum', 'get'],
        sampleDecodables: ['pig', 'dig'],
        svgIcon: createSvgIcon('Girl', 'M50 20 C35 20 28 32 28 45 C28 58 36 68 50 68 C64 68 72 58 72 45 L72 75 C72 88 58 92 45 92 L42 82 C52 82 60 80 60 72 L60 62 C56 66 50 68 45 68 Z'),
    },
    'o': {
        position: 10,
        grapheme: 'o',
        stageName: 'Set 1',
        keyword: 'Orange',
        formationRhyme: 'All the way around the juicy orange.',
        purePhonemeHint: 'Round lips: short /o/ (as in "on", "off").',
        exampleWords: ['on', 'off', 'ox', 'odd'],
        sampleDecodables: ['pot', 'top'],
        svgIcon: createSvgIcon('Orange', 'M50 20 A 30 30 0 1 0 50 80 A 30 30 0 1 0 50 20 Z', 'M50 20 C48 10 55 8 58 6 C56 12 54 16 50 20 Z'),
    },
    'c': {
        position: 11,
        grapheme: 'c',
        stageName: 'Set 1',
        keyword: 'Caterpillar',
        formationRhyme: 'Curl gently around the crawling caterpillar.',
        purePhonemeHint: 'Crisp throat click: unvoiced /k/. Avoid saying "cuh".',
        exampleWords: ['cat', 'cup', 'cot', 'can'],
        sampleDecodables: ['cat', 'can'],
        svgIcon: createSvgIcon('Caterpillar', 'M75 35 C68 24 55 20 45 20 C28 20 18 34 18 50 C18 66 28 80 45 80 C56 80 70 75 76 65 L66 58 C62 66 54 70 45 70 C34 70 28 62 28 50 C28 38 34 30 45 30 C52 30 62 33 66 42 Z'),
    },
    'k': {
        position: 12,
        grapheme: 'k',
        stageName: 'Set 1',
        keyword: 'Kangaroo',
        formationRhyme: 'Down the kangaroo\'s body, tail, and kick out her leg.',
        purePhonemeHint: 'Same pure sound as "c": unvoiced /k/. Avoid "kuh".',
        exampleWords: ['kit', 'kid', 'keg', 'kiss'],
        sampleDecodables: ['kit', 'kid'],
        svgIcon: createSvgIcon('Kangaroo', 'M35 15 L45 15 L45 85 L35 85 Z M45 52 L68 25 L80 25 L55 55 L82 85 L68 85 Z'),
    },
    'u': {
        position: 13,
        grapheme: 'u',
        stageName: 'Set 1',
        keyword: 'Umbrella',
        formationRhyme: 'Down and under the umbrella, up to the top, and down to the puddle.',
        purePhonemeHint: 'Short open vowel: /u/ (as in "up").',
        exampleWords: ['up', 'us', 'under', 'ugly'],
        sampleDecodables: ['cup', 'mud'],
        svgIcon: createSvgIcon('Umbrella', 'M50 20 C25 20 15 45 15 52 L85 52 C85 45 75 20 50 20 Z M46 52 L46 80 C46 86 52 88 56 86 L58 78 C54 78 52 76 52 72 L52 52 Z'),
    },
    'b': {
        position: 14,
        grapheme: 'b',
        stageName: 'Set 1',
        keyword: 'Boot',
        formationRhyme: 'Down the tall boot, up and around the round toe.',
        purePhonemeHint: 'Voiced lip pop: clipped /b/. Avoid saying "buh".',
        exampleWords: ['bat', 'bed', 'bin', 'box'],
        sampleDecodables: ['bat', 'bin'],
        svgIcon: createSvgIcon('Boot', 'M35 15 L50 15 L50 60 L75 60 C82 60 85 68 85 75 L85 85 L30 85 C25 85 25 78 30 75 L35 75 Z'),
    },
    'f': {
        position: 15,
        grapheme: 'f',
        stageName: 'Set 1',
        keyword: 'Flower',
        formationRhyme: 'Curve over the flower, straight down the stem, and cross the leaves.',
        purePhonemeHint: 'Continuous air through top teeth on lower lip: /f/. Avoid "fuh".',
        exampleWords: ['fan', 'fit', 'fog', 'fun'],
        sampleDecodables: ['fan', 'fit'],
        svgIcon: createSvgIcon('Flower', 'M46 35 L54 35 L54 85 L46 85 Z M50 35 A 15 15 0 1 0 50 15 A 15 15 0 1 0 50 35 Z M35 55 C42 50 50 55 50 55 C50 55 45 65 35 55 Z M65 55 C58 50 50 55 50 55 C50 55 55 65 65 55 Z'),
    },
    'e': {
        position: 16,
        grapheme: 'e',
        stageName: 'Set 1',
        keyword: 'Egg',
        formationRhyme: 'Cut across the egg, and scoop out the round shell.',
        purePhonemeHint: 'Short open sound: /e/ (as in "egg", "elbow").',
        exampleWords: ['egg', 'end', 'elf', 'elk'],
        sampleDecodables: ['bed', 'red'],
        svgIcon: createSvgIcon('Egg', 'M50 15 C30 15 25 38 25 55 C25 75 35 85 50 85 C65 85 75 75 75 55 C75 38 70 15 50 15 Z'),
    },
    'l': {
        position: 17,
        grapheme: 'l',
        stageName: 'Set 1',
        keyword: 'Ladder',
        formationRhyme: 'Straight down the tall ladder, with a little curl at the end.',
        purePhonemeHint: 'Continuous sound with tongue behind front teeth: /l/. Avoid "luh".',
        exampleWords: ['leg', 'lip', 'log', 'lot'],
        sampleDecodables: ['leg', 'lap'],
        svgIcon: createSvgIcon('Ladder', 'M32 15 L40 15 L40 85 L32 85 Z M60 15 L68 15 L68 85 L60 85 Z M40 30 L60 30 L60 36 L40 36 Z M40 50 L60 50 L60 56 L40 56 Z M40 70 L60 70 L60 76 L40 76 Z'),
    },
    'h': {
        position: 18,
        grapheme: 'h',
        stageName: 'Set 1',
        keyword: 'Horse',
        formationRhyme: 'Down the horse\'s neck to his hooves, up and over his back.',
        purePhonemeHint: 'Whispered breath of air: /h/ (like panting gently). Avoid "huh".',
        exampleWords: ['hat', 'hop', 'hut', 'hen'],
        sampleDecodables: ['hat', 'hop'],
        svgIcon: createSvgIcon('Horse', 'M30 20 L50 20 L55 35 L40 45 L40 85 L30 85 Z M40 50 L75 50 L80 85 L70 85 L68 62 L48 62 L48 85 L40 85 Z'),
    },
    'r': {
        position: 19,
        grapheme: 'r',
        stageName: 'Set 1',
        keyword: 'Robot',
        formationRhyme: 'Down the robot\'s back, back up and curl over his arm.',
        purePhonemeHint: 'Growl gently with tongue curved: /r/. Avoid saying "ruh".',
        exampleWords: ['red', 'run', 'rip', 'rod'],
        sampleDecodables: ['red', 'run'],
        svgIcon: createSvgIcon('Robot', 'M30 35 L70 35 L70 80 L30 80 Z M40 45 A 5 5 0 1 0 40 55 A 5 5 0 1 0 40 45 Z M60 45 A 5 5 0 1 0 60 55 A 5 5 0 1 0 60 45 Z M40 68 L60 68 Z M48 20 L52 20 L52 35 L48 35 Z', 'M50 15 A 5 5 0 1 0 50 25 A 5 5 0 1 0 50 15 Z'),
    },
    'j': {
        position: 20,
        grapheme: 'j',
        stageName: 'Set 1',
        keyword: 'Jellyfish',
        formationRhyme: 'Down the jellyfish, curl his long tail, and a dot for his head.',
        purePhonemeHint: 'Clipped sound: /j/ (as in "jump"). Avoid saying "juh".',
        exampleWords: ['jam', 'jog', 'jet', 'jug'],
        sampleDecodables: ['jam', 'jog'],
        svgIcon: createSvgIcon('Jellyfish', 'M25 45 C25 25 75 25 75 45 C75 52 25 52 25 45 Z M35 52 C35 75 40 85 35 90 M50 52 C50 75 55 85 50 90 M65 52 C65 75 70 85 65 90'),
    },
    'v': {
        position: 21,
        grapheme: 'v',
        stageName: 'Set 1',
        keyword: 'Valley',
        formationRhyme: 'Down into the deep valley, and back up the other side.',
        purePhonemeHint: 'Vibrate top teeth on bottom lip: continuous voiced /v/. Avoid "vuh".',
        exampleWords: ['van', 'vet', 'vat', 'vest'],
        sampleDecodables: ['van', 'vet'],
        svgIcon: createSvgIcon('Valley', 'M15 30 L45 80 L75 30 Z M35 30 L55 70 L75 30 L95 85 L15 85 Z'),
    },
    'y': {
        position: 22,
        grapheme: 'y',
        stageName: 'Set 1',
        keyword: 'Yak',
        formationRhyme: 'Down one horn, up the other, and down under the yak\'s chin.',
        purePhonemeHint: 'Short consonant sound: /y/ (as in "yes", "yellow"). Avoid "yuh".',
        exampleWords: ['yes', 'yak', 'yell', 'yum'],
        sampleDecodables: ['yes', 'yak'],
        svgIcon: createSvgIcon('Yak', 'M35 45 C35 35 65 35 65 45 L65 75 C65 82 35 82 35 75 Z M25 25 C30 35 40 40 40 45 M75 25 C70 35 60 40 60 45'),
    },
    'w': {
        position: 23,
        grapheme: 'w',
        stageName: 'Set 1',
        keyword: 'Waves',
        formationRhyme: 'Down, up, down, and up across the splashing waves.',
        purePhonemeHint: 'Round lips and blow: /w/ (as in "win", "wet"). Avoid saying "wuh".',
        exampleWords: ['win', 'wet', 'wig', 'web'],
        sampleDecodables: ['win', 'wet'],
        svgIcon: createSvgIcon('Waves', 'M10 50 C20 40 30 60 40 50 C50 40 60 60 70 50 C80 40 90 60 100 50 L100 80 L10 80 Z M10 35 C20 25 30 45 40 35 C50 25 60 45 70 35 C80 25 90 45 100 35'),
    },
    'z': {
        position: 24,
        grapheme: 'z',
        stageName: 'Set 1',
        keyword: 'Zip',
        formationRhyme: 'Zig-zag-zig across the shiny zip.',
        purePhonemeHint: 'Voiced buzzing sound: /z/ (like a buzzing bee). Avoid "zuh".',
        exampleWords: ['zip', 'zoo', 'zap', 'zag'],
        sampleDecodables: ['zip', 'zap'],
        svgIcon: createSvgIcon('Zip', 'M40 15 L60 15 L60 85 L40 85 Z M46 25 L54 25 M46 35 L54 35 M46 45 L54 45 M46 55 L54 55 M46 65 L54 65 M46 75 L54 75'),
    },
    'x': {
        position: 25,
        grapheme: 'x',
        stageName: 'Set 1',
        keyword: 'Fox',
        formationRhyme: 'Slide down one arm, slide down the other to make an X.',
        purePhonemeHint: 'Blended consonant sound: /k/ + /s/ = /ks/ (as in "box", "fox").',
        exampleWords: ['fox', 'box', 'six', 'fix'],
        sampleDecodables: ['six', 'fix'],
        svgIcon: createSvgIcon('Fox', 'M50 35 L30 20 L35 45 C25 55 35 75 50 75 C65 75 75 55 65 45 L70 20 Z', 'M45 52 A 3 3 0 1 0 45 58 A 3 3 0 1 0 45 52 Z M55 52 A 3 3 0 1 0 55 58 A 3 3 0 1 0 55 52 Z'),
    },
    'sh': {
        position: 26,
        grapheme: 'sh',
        stageName: 'Set 1',
        keyword: 'Ship',
        formationRhyme: 'Quiet on the ship: two letters make one sound /sh/.',
        purePhonemeHint: 'Finger to lips: continuous unvoiced /sh/ (as in "hush", "ship").',
        exampleWords: ['ship', 'shop', 'fish', 'shed'],
        sampleDecodables: ['ship', 'shop'],
        svgIcon: createSvgIcon('Ship', 'M20 60 L80 60 L70 80 L30 80 Z M50 20 L50 60 M50 25 L75 45 L50 45 Z'),
    },
    'th': {
        position: 27,
        grapheme: 'th',
        stageName: 'Set 1',
        keyword: 'Thumb',
        formationRhyme: 'Thumb sticking up: two letters say /th/.',
        purePhonemeHint: 'Tongue between teeth: unvoiced /th/ (as in "thin", "thumb").',
        exampleWords: ['thin', 'moth', 'bath', 'path'],
        sampleDecodables: ['thin', 'bath'],
        svgIcon: createSvgIcon('Thumb', 'M35 55 L35 85 L75 85 L75 55 Z M35 55 C35 35 45 20 55 20 C62 20 65 28 65 40 L65 55 Z'),
    },
    'ch': {
        position: 28,
        grapheme: 'ch',
        stageName: 'Set 1',
        keyword: 'Chair',
        formationRhyme: 'Choo-choo train sits on the chair: /ch/.',
        purePhonemeHint: 'Explosive unvoiced sound: /ch/ (as in "chin", "chop"). Avoid "chuh".',
        exampleWords: ['chin', 'chop', 'chat', 'chip'],
        sampleDecodables: ['chin', 'chop'],
        svgIcon: createSvgIcon('Chair', 'M35 20 L45 20 L45 55 L75 55 L75 85 L65 85 L65 65 L45 65 L45 85 L35 85 Z'),
    },
    'qu': {
        position: 29,
        grapheme: 'qu',
        stageName: 'Set 1',
        keyword: 'Queen',
        formationRhyme: 'Round the queen\'s face, past her crown, with her faithful companion u.',
        purePhonemeHint: 'Quick blend: /kw/ (as in "queen", "quick").',
        exampleWords: ['quick', 'quit', 'quiz', 'quack'],
        sampleDecodables: ['quick', 'quiz'],
        svgIcon: createSvgIcon('Queen', 'M20 50 L30 25 L50 40 L70 25 L80 50 L80 65 L20 65 Z M30 70 L70 70 L70 78 L30 78 Z', 'M50 40 A 4 4 0 1 0 50 48 A 4 4 0 1 0 50 40 Z'),
    },
    'ng': {
        position: 30,
        grapheme: 'ng',
        stageName: 'Set 1',
        keyword: 'Ring',
        formationRhyme: 'A shiny golden ring on a string: /ng/.',
        purePhonemeHint: 'Back of tongue against roof: continuous nasal /ng/ (as in "sing"). Avoid "ng-guh".',
        exampleWords: ['ring', 'sing', 'song', 'wing'],
        sampleDecodables: ['ring', 'sing'],
        svgIcon: createSvgIcon('Ring', 'M50 35 A 25 25 0 1 0 50 85 A 25 25 0 1 0 50 35 Z M50 45 A 15 15 0 1 1 50 75 A 15 15 0 1 1 50 45 Z', 'M50 18 L60 30 L40 30 Z'),
    },
    'nk': {
        position: 31,
        grapheme: 'nk',
        stageName: 'Set 1',
        keyword: 'Sink',
        formationRhyme: 'Wash with pink soap in the shiny sink: /nk/.',
        purePhonemeHint: 'Nasal tap into click: /nk/ (as in "think", "sink").',
        exampleWords: ['sink', 'wink', 'pink', 'bank'],
        sampleDecodables: ['sink', 'pink'],
        svgIcon: createSvgIcon('Sink', 'M20 40 L80 40 L75 75 C75 80 70 82 50 82 C30 82 25 80 25 75 Z M45 20 L55 20 L55 35 L45 35 Z'),
    },
    // ── Set 2: Long vowels & diphthongs (positions 32-41) ──
    'ee': {
        position: 32,
        grapheme: 'ee',
        stageName: 'Set 2',
        keyword: 'Tree',
        formationRhyme: 'What can you see? A tall green tree!',
        purePhonemeHint: 'Long vowel sound: /ee/ (as in "see", "tree"). Smile broadly.',
        exampleWords: ['see', 'tree', 'green', 'meet'],
        sampleDecodables: ['see', 'meet'],
        svgIcon: createSvgIcon('Tree', 'M45 55 L55 55 L55 85 L45 85 Z M50 15 C30 15 25 40 35 55 C45 60 55 60 65 55 C75 40 70 15 50 15 Z'),
    },
    'igh': {
        position: 33,
        grapheme: 'igh',
        stageName: 'Set 2',
        keyword: 'Night',
        formationRhyme: 'Fly so high in the starry night sky.',
        purePhonemeHint: 'Long vowel diphthong: /igh/ (as in "high", "night").',
        exampleWords: ['high', 'night', 'light', 'bright'],
        sampleDecodables: ['high', 'night'],
        svgIcon: createSvgIcon('Night', 'M65 25 C45 25 35 45 40 65 C45 80 60 85 75 80 C50 90 25 70 25 45 C25 25 45 15 65 25 Z', 'M75 25 L78 35 L88 38 L80 44 L82 54 L74 48 L66 54 L68 44 L60 38 L70 35 Z'),
    },
    'oa': {
        position: 34,
        grapheme: 'oa',
        stageName: 'Set 2',
        keyword: 'Boat',
        formationRhyme: 'Look at the playful goat in a boat.',
        purePhonemeHint: 'Long vowel: /oa/ (as in "boat", "coat").',
        exampleWords: ['boat', 'coat', 'road', 'goat'],
        sampleDecodables: ['boat', 'coat'],
        svgIcon: createSvgIcon('Boat', 'M15 60 L85 60 L75 80 L25 80 Z M50 20 L50 60 M50 25 L75 45 L50 45 Z'),
    },
    'oo': {
        position: 35,
        grapheme: 'oo',
        stageName: 'Set 2',
        keyword: 'Moon',
        formationRhyme: 'Zoom to the moon, or read in a book.',
        purePhonemeHint: 'Long /oo/ (as in "moon") or short /oo/ (as in "book").',
        exampleWords: ['moon', 'zoo', 'book', 'look'],
        sampleDecodables: ['moon', 'book'],
        svgIcon: createSvgIcon('Moon', 'M60 20 C40 20 30 40 35 60 C40 75 55 80 70 75 C48 85 25 65 25 42 C25 22 45 12 60 20 Z'),
    },
    'ar': {
        position: 36,
        grapheme: 'ar',
        stageName: 'Set 2',
        keyword: 'Car',
        formationRhyme: 'Start the engine and drive the red car.',
        purePhonemeHint: 'Open throat sound: /ar/ (as in "car", "star"). Say "ah".',
        exampleWords: ['car', 'star', 'park', 'hard'],
        sampleDecodables: ['car', 'star'],
        svgIcon: createSvgIcon('Car', 'M20 50 L35 30 L65 30 L80 50 L85 55 L85 70 L15 70 L15 55 Z M30 70 A 8 8 0 1 0 30 86 A 8 8 0 1 0 30 70 Z M70 70 A 8 8 0 1 0 70 86 A 8 8 0 1 0 70 70 Z'),
    },
    'or': {
        position: 37,
        grapheme: 'or',
        stageName: 'Set 2',
        keyword: 'Door',
        formationRhyme: 'Shut the wooden door, don\'t knock anymore.',
        purePhonemeHint: 'Deep vowel sound: /or/ (as in "fork", "door").',
        exampleWords: ['fork', 'horn', 'cork', 'storm'],
        sampleDecodables: ['fork', 'horn'],
        svgIcon: createSvgIcon('Door', 'M30 15 L70 15 L70 85 L30 85 Z M60 50 A 4 4 0 1 0 60 58 A 4 4 0 1 0 60 50 Z'),
    },
    'air': {
        position: 38,
        grapheme: 'air',
        stageName: 'Set 2',
        keyword: 'Chair',
        formationRhyme: 'Fair and square, sit on the chair.',
        purePhonemeHint: 'Gliding trigraph sound: /air/ (as in "chair", "fair").',
        exampleWords: ['chair', 'hair', 'fair', 'stair'],
        sampleDecodables: ['chair', 'hair'],
        svgIcon: createSvgIcon('Chair', 'M35 25 L45 25 L45 55 L75 55 L75 85 L65 85 L65 65 L45 65 L45 85 L35 85 Z'),
    },
    'ir': {
        position: 39,
        grapheme: 'ir',
        stageName: 'Set 2',
        keyword: 'Bird',
        formationRhyme: 'Twirl and whirl like a singing bird.',
        purePhonemeHint: 'Central vowel: /ir/ (as in "bird", "girl", "twirl").',
        exampleWords: ['bird', 'girl', 'twirl', 'shirt'],
        sampleDecodables: ['bird', 'girl'],
        svgIcon: createSvgIcon('Bird', 'M20 50 C25 35 45 35 55 45 C65 45 75 35 85 45 C75 65 55 70 40 70 C30 70 20 60 20 50 Z', 'M80 42 L90 45 L80 48 Z'),
    },
    'ou': {
        position: 40,
        grapheme: 'ou',
        stageName: 'Set 2',
        keyword: 'Cloud',
        formationRhyme: 'Shout it out loud to the fluffy cloud.',
        purePhonemeHint: 'Diphthong glide: /ou/ (as in "shout", "cloud", "out").',
        exampleWords: ['out', 'shout', 'cloud', 'loud'],
        sampleDecodables: ['out', 'shout'],
        svgIcon: createSvgIcon('Cloud', 'M30 65 C20 65 15 55 20 45 C22 35 35 32 40 38 C45 25 65 25 72 35 C80 35 85 45 82 55 C88 60 82 65 75 65 Z'),
    },
    'oy': {
        position: 41,
        grapheme: 'oy',
        stageName: 'Set 2',
        keyword: 'Toy',
        formationRhyme: 'A wonderful toy for every girl and boy.',
        purePhonemeHint: 'Diphthong: /oy/ (as in "boy", "toy", "enjoy").',
        exampleWords: ['boy', 'toy', 'enjoy', 'joy'],
        sampleDecodables: ['boy', 'toy'],
        svgIcon: createSvgIcon('Toy', 'M30 40 L70 40 L70 80 L30 80 Z M30 55 L70 55 M45 40 L45 80 M50 25 C45 25 40 30 45 40 C55 40 55 25 50 25 Z'),
    },
    // ── Set 3: Alternative spelling graphemes (positions 42-50) ──
    'ai': {
        position: 42,
        grapheme: 'ai',
        stageName: 'Set 3',
        keyword: 'Train',
        formationRhyme: 'Rain, rain, wait for the train.',
        purePhonemeHint: 'Long vowel sound: /ai/ (as in "train", "rain").',
        exampleWords: ['train', 'rain', 'paint', 'snail'],
        sampleDecodables: ['train', 'rain'],
        svgIcon: createSvgIcon('Train', 'M25 30 L75 30 L75 75 L25 75 Z M30 75 A 6 6 0 1 0 30 87 A 6 6 0 1 0 30 75 Z M70 75 A 6 6 0 1 0 70 87 A 6 6 0 1 0 70 75 Z M35 40 L65 40 L65 55 L35 55 Z'),
    },
    'ay': {
        position: 43,
        grapheme: 'ay',
        stageName: 'Set 3',
        keyword: 'Play',
        formationRhyme: 'May I go out and play today?',
        purePhonemeHint: 'Long vowel sound at end of words: /ay/ (as in "play", "day").',
        exampleWords: ['play', 'day', 'say', 'way'],
        sampleDecodables: ['play', 'day'],
        svgIcon: createSvgIcon('Play', 'M30 20 L80 50 L30 80 Z', 'M50 15 A 6 6 0 1 0 50 27 A 6 6 0 1 0 50 15 Z'),
    },
    'ea': {
        position: 44,
        grapheme: 'ea',
        stageName: 'Set 3',
        keyword: 'Tea',
        formationRhyme: 'A warm cup of tea by the calm blue sea.',
        purePhonemeHint: 'Long vowel: /ea/ (as in "tea", "sea", "eat").',
        exampleWords: ['tea', 'sea', 'eat', 'meat'],
        sampleDecodables: ['sea', 'eat'],
        svgIcon: createSvgIcon('Tea', 'M25 40 L75 40 L70 80 L30 80 Z M75 48 C85 48 85 68 72 68 M40 25 C40 35 45 35 45 40 M55 25 C55 35 60 35 60 40'),
    },
    'oi': {
        position: 45,
        grapheme: 'oi',
        stageName: 'Set 3',
        keyword: 'Coin',
        formationRhyme: 'Find a shiny gold coin inside the soil.',
        purePhonemeHint: 'Diphthong: /oi/ (as in "coin", "join", "soil").',
        exampleWords: ['coin', 'join', 'soil', 'boil'],
        sampleDecodables: ['coin', 'join'],
        svgIcon: createSvgIcon('Coin', 'M50 20 A 30 30 0 1 0 50 80 A 30 30 0 1 0 50 20 Z M50 28 A 22 22 0 1 1 50 72 A 22 22 0 1 1 50 28 Z', 'M46 40 L54 40 L54 60 L46 60 Z'),
    },
    'a-e': {
        position: 46,
        grapheme: 'a-e',
        stageName: 'Set 3',
        keyword: 'Cake',
        formationRhyme: 'Make a tasty cake with sweet icing.',
        purePhonemeHint: 'Split digraph: /a-e/ (as in "cake", "make", "name").',
        exampleWords: ['cake', 'make', 'name', 'lake'],
        sampleDecodables: ['cake', 'make'],
        svgIcon: createSvgIcon('Cake', 'M20 50 L80 50 L80 80 L20 80 Z M30 35 L70 35 L70 50 L30 50 Z M48 20 L52 20 L52 35 L48 35 Z', 'M50 15 A 3 3 0 1 0 50 21 A 3 3 0 1 0 50 15 Z'),
    },
    'e-e': {
        position: 47,
        grapheme: 'e-e',
        stageName: 'Set 3',
        keyword: 'Athlete',
        formationRhyme: 'Pete the athlete runs with speedy feet.',
        purePhonemeHint: 'Split digraph: /e-e/ (as in "these", "theme", "Pete").',
        exampleWords: ['these', 'theme', 'Pete', 'complete'],
        sampleDecodables: ['these', 'theme'],
        svgIcon: createSvgIcon('Athlete', 'M40 30 L60 30 L55 60 L68 85 L56 85 L48 65 L40 85 L28 85 Z', 'M50 15 A 8 8 0 1 0 50 31 A 8 8 0 1 0 50 15 Z'),
    },
    'i-e': {
        position: 48,
        grapheme: 'i-e',
        stageName: 'Set 3',
        keyword: 'Bike',
        formationRhyme: 'Ride a nice white bike with a bright smile.',
        purePhonemeHint: 'Split digraph: /i-e/ (as in "bike", "time", "smile").',
        exampleWords: ['bike', 'time', 'smile', 'white'],
        sampleDecodables: ['bike', 'time'],
        svgIcon: createSvgIcon('Bike', 'M25 65 A 12 12 0 1 0 25 89 A 12 12 0 1 0 25 65 Z M75 65 A 12 12 0 1 0 75 89 A 12 12 0 1 0 75 65 Z M25 77 L48 77 L62 55 L38 55 Z M48 77 L56 50 L70 50 M75 77 L62 55'),
    },
    'o-e': {
        position: 49,
        grapheme: 'o-e',
        stageName: 'Set 3',
        keyword: 'Home',
        formationRhyme: 'Dial the phone and head safely home.',
        purePhonemeHint: 'Split digraph: /o-e/ (as in "home", "bone", "hope").',
        exampleWords: ['home', 'bone', 'phone', 'rose'],
        sampleDecodables: ['home', 'bone'],
        svgIcon: createSvgIcon('Home', 'M50 20 L85 50 L75 50 L75 80 L25 80 L25 50 L15 50 Z M42 55 L58 55 L58 80 L42 80 Z'),
    },
    'u-e': {
        position: 50,
        grapheme: 'u-e',
        stageName: 'Set 3',
        keyword: 'Cube',
        formationRhyme: 'Turn the huge shiny cube in the light.',
        purePhonemeHint: 'Split digraph: /u-e/ (as in "cube", "huge", "tune").',
        exampleWords: ['cube', 'huge', 'tune', 'mule'],
        sampleDecodables: ['cube', 'huge'],
        svgIcon: createSvgIcon('Cube', 'M50 20 L85 38 L50 55 L15 38 Z M15 38 L50 55 L50 85 L15 68 Z M85 38 L50 55 L50 85 L85 68 Z'),
    },
};
/**
 * Returns the public HTTPS URL for the pure-phoneme audio player for a grapheme.
 * Used for QR codes on printable sound cards so scanning with any phone
 * immediately launches the audio player web page without login barriers.
 */
function getInkStepPublicAudioUrl(grapheme) {
    return `https://eduwrite.app/phonics/sound/${encodeURIComponent(grapheme.toLowerCase())}`;
}
/**
 * Looks up mnemonic data by grapheme string or sequence position (1-50).
 */
function getInkStepMnemonic(graphemeOrPosition) {
    if (typeof graphemeOrPosition === 'number') {
        return Object.values(exports.INKSTEP_MNEMONICS).find((m) => m.position === graphemeOrPosition);
    }
    return exports.INKSTEP_MNEMONICS[graphemeOrPosition.toLowerCase()];
}
/**
 * Returns all 50 mnemonic items sorted by position.
 */
function getAllInkStepMnemonics() {
    return Object.values(exports.INKSTEP_MNEMONICS).sort((a, b) => a.position - b.position);
}
/**
 * Returns mnemonics filtered by stage ('Set 1', 'Set 2', 'Set 3').
 */
function getInkStepMnemonicsForStage(stageName) {
    return getAllInkStepMnemonics().filter((m) => m.stageName === stageName);
}
